/**
 * EoT Claim Builder — Review & Tracking.
 *
 * Owners, the contractual dates that matter, in-app reminders when one is close,
 * and a sign-off a reviewer has to give before the claim can be marked submitted.
 */
import { useCallback, useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save, Send, Check, Undo2, BellRing, History } from 'lucide-react';

const STAGE_TONE = {
  DRAFT: 'text-muted-foreground', IN_REVIEW: 'text-[#6B5CE7]', RETURNED: 'text-amber-600',
  ACCEPTED: 'text-emerald-600', SUBMITTED: 'text-emerald-600', REJECTED: 'text-red-500',
};

export default function ReviewTrackingTab({ claimId, onChanged }) {
  const [t, setT] = useState(null);
  const [form, setForm] = useState({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    const { data } = await api.get(`/eot/claims/${claimId}/tracking`);
    setT(data);
    setForm({
      owner: data.owner || '', reviewer: data.reviewer || '', remindBeforeDays: data.remindBeforeDays ?? 7,
      referenceNumber: data.referenceNumber || '', notes: data.notes || '',
      ...Object.fromEntries((data.dateFields || []).flatMap(f => [
        [f.key, data[f.key] || ''], [f.key.replace('DueDate', 'DoneDate'), data[f.key.replace('DueDate', 'DoneDate')] || ''],
      ])),
    });
  }, [claimId]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  const save = async () => {
    setBusy('save');
    try {
      const { data } = await api.put(`/eot/claims/${claimId}/tracking`, form);
      setT(data);
      toast.success('Tracking saved');
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
    finally { setBusy(''); }
  };

  const act = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/eot/claims/${claimId}/tracking/review`, {
        action, note, reviewer: form.reviewer, referenceNumber: form.referenceNumber,
      });
      setT(data);
      setNote('');
      toast.success({
        SEND_FOR_REVIEW: 'Sent for review', ACCEPT: 'Accepted — the pack can now be marked submitted',
        RETURN: 'Returned to the drafter', SUBMIT: 'Marked as submitted', REOPEN: 'Reopened as a draft',
      }[action]);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not do that'); }
    finally { setBusy(''); }
  };

  if (!t) return <div className="pt-6 text-xs text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-4 pt-4" data-testid="eot-tracking-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-foreground font-medium">
            Review &amp; tracking —{' '}
            <span className={STAGE_TONE[t.stage] || 'text-foreground'} data-testid="eot-tracking-stage">
              {String(t.stage || 'DRAFT').replaceAll('_', ' ').toLowerCase()}
            </span>
          </p>
          <p className="text-[11px] text-muted-foreground">
            A reviewer has to accept the pack before the claim can be marked as submitted.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={save} disabled={busy === 'save'} data-testid="eot-save-tracking">
            {busy === 'save' ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
            Save
          </Button>
          {['DRAFT', 'RETURNED'].includes(t.stage) && (
            <Button size="sm" onClick={() => act('SEND_FOR_REVIEW')} disabled={busy === 'SEND_FOR_REVIEW'}
                    data-testid="eot-send-review" className="bg-gradient-to-r from-[#1E4E8C] to-[#6B5CE7]">
              <Send className="w-3.5 h-3.5 mr-1.5" />Send for review
            </Button>
          )}
          {t.stage === 'IN_REVIEW' && (
            <>
              <Button size="sm" onClick={() => act('ACCEPT')} disabled={busy === 'ACCEPT'} data-testid="eot-accept">
                <Check className="w-3.5 h-3.5 mr-1.5" />Accept
              </Button>
              <Button size="sm" variant="outline" onClick={() => act('RETURN')} disabled={busy === 'RETURN'}
                      data-testid="eot-return">
                <Undo2 className="w-3.5 h-3.5 mr-1.5" />Return
              </Button>
            </>
          )}
          {t.canSubmit && (
            <Button size="sm" onClick={() => act('SUBMIT')} disabled={busy === 'SUBMIT'} data-testid="eot-submit">
              <Send className="w-3.5 h-3.5 mr-1.5" />Mark as submitted
            </Button>
          )}
          {t.stage === 'SUBMITTED' && (
            <Button size="sm" variant="outline" onClick={() => act('REOPEN')} data-testid="eot-reopen">Reopen</Button>
          )}
        </div>
      </div>

      {(t.reminders || []).length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3" data-testid="eot-reminders">
          <p className="text-xs text-foreground font-medium flex items-center gap-1.5">
            <BellRing className="w-3.5 h-3.5 text-amber-500" />Dates needing attention
          </p>
          {t.reminders.map(r => (
            <p key={r.key} className={`text-[11px] mt-1 ${r.severity === 'OVERDUE' ? 'text-red-500' : 'text-amber-600'}`}
               data-testid={`eot-reminder-${r.key}`}>{r.message}</p>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-2">
        {[['owner', 'Claim owner'], ['reviewer', 'Reviewer who must accept'], ['referenceNumber', 'Our reference']]
          .map(([key, label]) => (
            <div key={key}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
              <Input value={form[key] || ''} onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                     className="h-8 text-xs" data-testid={`eot-tracking-${key}`} />
            </div>
          ))}
      </div>

      <div className="rounded-lg border border-border p-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-xs text-foreground font-medium">Key dates</p>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-muted-foreground">Remind me</span>
            <Input type="number" value={form.remindBeforeDays ?? 7} className="h-7 w-16 text-xs"
                   onChange={e => setForm(p => ({ ...p, remindBeforeDays: Number(e.target.value) }))}
                   data-testid="eot-remind-days" />
            <span className="text-[10px] text-muted-foreground">days before</span>
          </div>
        </div>
        <table className="w-full text-xs" data-testid="eot-dates-table">
          <thead className="text-muted-foreground uppercase text-[10px]">
            <tr>{['Date', 'Due', 'Done'].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
          </thead>
          <tbody>
            {(t.dateFields || []).map(f => {
              const doneKey = f.key.replace('DueDate', 'DoneDate');
              return (
                <tr key={f.key} className="border-t border-border" data-testid={`eot-date-${f.key}`}>
                  <td className="px-2 py-1.5 text-foreground">{f.label}</td>
                  <td className="px-2 py-1.5">
                    <Input type="date" value={form[f.key] || ''} className="h-7 w-36 text-[11px]"
                           onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                           data-testid={`eot-due-${f.key}`} />
                  </td>
                  <td className="px-2 py-1.5">
                    <Input type="date" value={form[doneKey] || ''} className="h-7 w-36 text-[11px]"
                           onChange={e => setForm(p => ({ ...p, [doneKey]: e.target.value }))}
                           data-testid={`eot-done-${f.key}`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-border p-3 space-y-2">
        <p className="text-xs text-foreground font-medium">Note for the next action</p>
        <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} className="text-xs"
                  placeholder="What the reviewer should know, or why it is being returned…"
                  data-testid="eot-review-note" />
        <Textarea rows={3} value={form.notes || ''} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  className="text-xs" placeholder="Running notes on this claim" data-testid="eot-tracking-notes" />
      </div>

      {(t.history || []).length > 0 && (
        <div className="rounded-lg border border-border p-3" data-testid="eot-tracking-history">
          <p className="text-xs text-foreground font-medium flex items-center gap-1.5 mb-1">
            <History className="w-3.5 h-3.5 text-[#6B5CE7]" />Audit trail
          </p>
          {[...t.history].reverse().map((h, i) => (
            <p key={i} className="text-[10px] text-muted-foreground">
              {String(h.at).slice(0, 16).replace('T', ' ')} — {h.by} · {String(h.action).replaceAll('_', ' ').toLowerCase()}
              {' '}({String(h.from).toLowerCase()} → {String(h.to).toLowerCase()}){h.note ? ` — “${h.note}”` : ''}
            </p>
          ))}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground">
        <Badge variant="outline" className="text-[9px] mr-1">in-app reminders</Badge>
        Reminders show here and on the claim; no email is sent.
      </p>
    </div>
  );
}
